import { useMemo } from 'react';

import { Accordion, Text } from '../../common/components';
import { t } from '../../common/i18n';
import { Deadline, DeadlineStatusKind } from '../types';
import { getStatusKind } from '../utils/deadlineStatus';
import DeadlineCard from './DeadlineCard';

const namespace = 'Deadlines';

interface DeadlineListProps {
  deadlines: Deadline[];
  onComplete: (deadline: Deadline, completed: boolean) => void;
  onEdit: (deadline: Deadline) => void;
  onDelete: (deadline: Deadline) => void;
}

type SectionKey = 'thisYear' | 'nextYear' | 'later' | 'older';

const sectionOrder: SectionKey[] = ['thisYear', 'nextYear', 'later'];

/** Pořadí karet uvnitř roční sekce: nejdřív co hoří, splněné nakonec. */
const statusRank: Record<DeadlineStatusKind, number> = { overdue: 0, soon: 1, upcoming: 2, completed: 3 };

/**
 * Zařazení do sekce podle roku splatnosti. Loňský NESPLNĚNÝ rest zůstává
 * v „Letos" (pořád se má řešit); vše ostatní z minulých let jde do „Starší"
 * (historie, defaultně sbalená), aby hlavní pohled nezarůstal.
 */
const sectionFor = (deadline: Deadline, currentYear: number): SectionKey => {
  const year = Number(deadline.dueDate.slice(0, 4));

  if (year > currentYear + 1) return 'later';
  if (year === currentYear + 1) return 'nextYear';
  if (year === currentYear) return 'thisYear';
  if (deadline.completedAt === null && year === currentYear - 1) return 'thisYear';

  return 'older';
};

export const DeadlineList: React.FC<DeadlineListProps> = ({ deadlines, onComplete, onEdit, onDelete }) => {
  const currentYear = new Date().getFullYear();

  const sections = useMemo(() => {
    const buckets: Record<SectionKey, Deadline[]> = { thisYear: [], nextYear: [], later: [], older: [] };
    const kinds = new Map<string, DeadlineStatusKind>();

    deadlines.forEach((deadline) => {
      kinds.set(deadline.id, getStatusKind(deadline));
      buckets[sectionFor(deadline, currentYear)].push(deadline);
    });

    sectionOrder.forEach((key) =>
      buckets[key].sort((a, b) => {
        const rank = statusRank[kinds.get(a.id) ?? 'upcoming'] - statusRank[kinds.get(b.id) ?? 'upcoming'];
        return rank !== 0 ? rank : a.dueDate.localeCompare(b.dueDate);
      })
    );

    // historie: nejnovější nahoře
    buckets.older.sort((a, b) => b.dueDate.localeCompare(a.dueDate));

    return buckets;
  }, [deadlines, currentYear]);

  if (deadlines.length === 0) {
    return (
      <Text as="div" className="DeadlineList__empty" size="body-medium" color="Neutral600">
        {t('emptyState', namespace)}
      </Text>
    );
  }

  const sectionTitle = (key: SectionKey): string => {
    if (key === 'thisYear') return t('yearGroups.thisYear', namespace, { year: currentYear });
    if (key === 'nextYear') return t('yearGroups.nextYear', namespace, { year: currentYear + 1 });
    if (key === 'older') return t('yearGroups.older', namespace);
    return t('yearGroups.later', namespace);
  };

  const renderCards = (items: Deadline[]) =>
    items.map((deadline) => (
      <DeadlineCard
        key={deadline.id}
        deadline={deadline}
        onComplete={onComplete}
        onEdit={onEdit}
        onDelete={onDelete}
      />
    ));

  // Příští rok defaultně sbalený – rozbalí se automaticky od prosince,
  // kdy už předseda plánuje další rok.
  const isDecember = new Date().getMonth() === 11;

  const renderCollapsible = (key: 'nextYear' | 'later' | 'older', isOpen: boolean) => {
    const items = sections[key];
    if (items.length === 0) return null;

    return (
      <Accordion
        title={`${sectionTitle(key)} · ${t('yearGroups.count', namespace, { count: items.length })}`}
        isOpen={isOpen}
        className="DeadlineList__collapsible"
      >
        {renderCards(items)}
      </Accordion>
    );
  };

  return (
    <div className="DeadlineList">
      {sections.thisYear.length > 0 && (
        <section className="DeadlineList__group">
          <Text as="div" className="DeadlineList__groupTitle" size="h3" color="Neutral800">
            {sectionTitle('thisYear')}
          </Text>
          {renderCards(sections.thisYear)}
        </section>
      )}

      {renderCollapsible('nextYear', isDecember)}
      {renderCollapsible('later', false)}
      {renderCollapsible('older', false)}
    </div>
  );
};

export default DeadlineList;
